import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion, useSpring, useMotionValueEvent } from "framer-motion";
import { Play, ArrowRight, Volume2, VolumeX } from "lucide-react";

/**
 * dentsuAMP — public landing (V3 · "the studio cut").
 * The HUL-family editorial language (Anton display, white paper bands,
 * numbered pillars, THE UNVEILING curtain) fused with the V2 cinematic
 * video wall — strictly black / white / red. Media stays ungated via
 * /api/public-assets, preload=none everywhere; first paint ships zero
 * video bytes.
 */

const ASSETS = "/api/public-assets";
const RED = "#E31937";

const WALL = ["reel-01", "reel-02", "reel-03", "reel-04", "reel-05", "reel-06", "reel-07", "reel-08", "reel-09", "reel-10", "reel-11"];

const ROW_PERFORMANCE = [
  { code: "P-01", file: "phonepe-gold.mp4", client: "PhonePe", title: "Gold" },
  { code: "P-02", file: "phonepe-silver.mp4", client: "PhonePe", title: "Silver" },
  { code: "P-03", file: "phonepe-youth-plan.mp4", client: "PhonePe", title: "Youth Plan" },
  { code: "P-04", file: "phonepe-sip.mp4", client: "PhonePe", title: "SIP" },
  { code: "P-05", file: "phonepe-vip-airport.mp4", client: "PhonePe", title: "VIP · Airport" },
  { code: "P-06", file: "phonepe-vip-hotel.mp4", client: "PhonePe", title: "VIP · Hotel" },
];

const ROW_BRAND = [
  { code: "B-01", file: "dentsu-amp-proof-01.mp4", client: "dentsuAMP", title: "Proof of Work" },
  { code: "B-02", file: "ownd-need-for-speed.mp4", client: "OWND!", title: "Need for Speed" },
  { code: "B-03", file: "ownd-frosting.mp4", client: "OWND!", title: "Frosting" },
  { code: "B-04", file: "dentsu-amp-proof-02.mp4", client: "dentsuAMP", title: "Studio Floor" },
  { code: "B-05", file: "dentsu-amp-proof-03.mp4", client: "dentsuAMP", title: "On Set" },
];

const RESULTS = [
  "7% ER achieved", "8x ROAS achieved", "1Mn followers reached",
  "5% CTR unlocked", "80% VTR unlocked", "₹39.7Cr EMV delivered",
  "300K growth in 4 months", "200+ videos monthly", "500M+ impressions",
  "30x engagement rate", "100+ phone launches", "2%+ CTR on performance",
  "0 to 600K followers", "₹50Cr pipeline built", "4.2% avg ER across brands",
  "Zero Hour IP created", "150+ films delivered", "3 Cannes shortlists",
];

const STATS = [
  { value: 500, suffix: "M+", label: "Impressions delivered" },
  { value: 150, suffix: "+", label: "Films produced" },
  { value: 50, suffix: "Cr+", label: "Revenue pipeline", prefix: "₹" },
  { value: 3, suffix: "", label: "Cannes shortlists" },
];

// The four woes every CMO knows by name — named sharply so they feel caught.
const WOES = [
  { n: "01", title: "Disintegrated campaigns", cost: "Five vendors, zero shared outcome. ROI bleeds 5–6X at every handoff between them." },
  { n: "02", title: "Unattributable results", cost: "Crores spent, a deck to show for it. When the CFO asks the sales you got, you don't have a number." },
  { n: "03", title: "Brand that doesn't sell", cost: "Beautiful films. Awards. Zero lift in offtake. Brand and performance fighting instead of compounding." },
  { n: "04", title: "Volume vs quality vs speed", cost: "The algorithm needs 50+ assets a month. Your agency needs 2 weeks per asset. You miss every wave." },
];

// The Four Solves — each woe, structurally answered. This is Outcomes as a Service.
const PILLARS = [
  { n: "01", title: "Research & Strategy", sub: "Proprietary tech and data across every market · 250+ parameters · synthesised signals that tell us what to make before we make it." },
  { n: "02", title: "Infrastructure for Scale", sub: "10 studios live, 10 more launching · in-house crew · 500+ performant assets a month at a unit cost the market can't match." },
  { n: "03", title: "Media Deployment", sub: "Custom dashboards · daily signals · lowest CPMs in the industry · full-funnel TOFU / MOFU / BOFU, deployed and read weekly." },
  { n: "04", title: "Iterative Data Science", sub: "Every asset scored on a performance rubric, tested and optimised · the data writes the next brief. Creative that compounds weekly." },
];

// Proof in pain → solve → number form. Metrics bracketed = drop in AMP's verified figures before launch.
const CASE_STUDIES = [
  {
    client: "PhonePe", tag: "Performance content at scale",
    woe: "Needed performance-grade creative at a volume no single agency could feed.",
    solve: "An always-on studio running funnel-mapped, scored assets every month — briefed, shot and optimised as one line.",
    metrics: [["[0 → 500M]", "views in [90 days]"], ["[₹X]", "per asset vs market"]],
  },
  {
    client: "Groww", tag: "Brand × performance, attributed",
    woe: "Heavy brand spend with no traceable line to acquisition.",
    solve: "Integrated brand and performance creative, attributed end-to-end inside the AMP OS.",
    metrics: [["[↓ X%]", "cost per acquisition"], ["[↑ YX]", "return on ad spend"]],
  },
  {
    client: "Myntra · Fashion", tag: "Full-funnel system",
    woe: "Hero films shone; the mid-funnel had a hole — PDPs and reels were an afterthought.",
    solve: "TOFU / MOFU / BOFU engineered as one system — runway to PDP to creator content.",
    metrics: [["[↑ X%]", "conversion lift"], ["[Y]", "full-funnel assets"]],
  },
];

const SERVICES = [
  { n: "01", name: "Strategy", desc: "Category intelligence, content science, brand planning" },
  { n: "02", name: "Production & Studios", desc: "In-house studios, 500+ films a month, 48-hour turnarounds" },
  { n: "03", name: "Performance", desc: "Media that learns weekly — CTR, ROAS, CPA accountable" },
  { n: "04", name: "Content Studio", desc: "Always-on social engines for brands that ship daily" },
];

const CLIENTS = ["MYNTRA FWD", "GROWW", "PHONEPE", "FLIPKART", "PANTALOONS", "NYKAA", "MYNTRA", "SHO", "METRO SHOES", "ZIVAME", "OWND"];

const OS_FEATURES = [
  ["BRIEF INTAKE", "Every ask lands in one queue — nothing lives in WhatsApp"],
  ["AI ASSISTED SCRIPTING", "First drafts in hours, learning from what performed"],
  ["STUDIO OPS", "Shoots, crew and edits scheduled like a factory line"],
  ["PERFORMANCE LOOP", "Weekly read-outs feed the next brief automatically"],
];

// Social proof — PLACEHOLDER. Swap [bracketed] attribution for a real, cleared quote.
const TESTIMONIAL = {
  quote: "AMP runs our entire content engine. Cost-per-asset dropped sharply and I stopped renewing two other vendors by month two — it's the closest thing to an in-house studio we've ever had.",
  name: "[Name]", role: "[VP Marketing / Head of Growth]", brand: "[Brand]",
};

// "Who runs your account" — PLACEHOLDER. Replace with real pod leads.
const POD = [
  { initials: "[ ]", name: "[Strategy Lead]", line: "[ex-brand] · built Dream Machine" },
  { initials: "[ ]", name: "[Studio Lead]", line: "[X] films shipped across [categories]" },
  { initials: "[ ]", name: "[Performance Lead]", line: "[X] in managed media · ROAS-accountable" },
];

// What happens after you raise your hand — removes the "emailing a void" fear.
const NEXT_STEPS = [
  ["01", "Tell us your volume", "Pick a tier or build a scope on the configurator — 60 seconds."],
  ["02", "A costed scope in 48 hours", "A real outcome model and the exact team — not a deck."],
  ["03", "A 30-minute working call", "We map your fragmentation tax in rupees. No obligation, no lock-in."],
];

const mono = { fontFamily: "'JetBrains Mono', monospace" };
const anton = { fontFamily: "'Anton', sans-serif", fontWeight: 400, letterSpacing: "-0.012em", lineHeight: 0.9, textTransform: "uppercase" };

function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let t0 = null;
    const step = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / duration, 1);
      setCount(Math.floor((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

const rise = {
  hidden: { opacity: 0, y: 34 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
};

const EASE = [0.16, 1, 0.3, 1];

/* Headline that "performs" — each line rises through a mask on entry.
   Speaks the shared hidden/show variant vocabulary, so it inherits the reveal
   from whichever section motion.div orchestrates it (no own viewport trigger). */
function RevealLines({ lines, dot, stagger = 0.09 }) {
  const reduce = useReducedMotion();
  return (
    <>
      {lines.map((ln, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span className="block"
            variants={{
              hidden: reduce ? { opacity: 0 } : { y: "125%" },
              show: reduce
                ? { opacity: 1, transition: { duration: 0.5, delay: i * stagger } }
                : { y: 0, transition: { duration: 1.0, delay: i * stagger, ease: EASE } },
            }}>
            {ln}{dot && i === lines.length - 1 && <span className="text-[#E31937]">.</span>}
          </motion.span>
        </span>
      ))}
    </>
  );
}

/* Contextual ambient gradient — drifts + fades in as its section crosses the
   viewport. The literal "gradients that kick in with parallax." */
function ParallaxAura({ color = "rgba(227,25,55,0.16)", pos = "50% 42%", size = "72% 60%", peak = 1, drift = 14 }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [`-${drift}%`, `${drift}%`]);
  const opacity = useTransform(scrollYProgress, [0, 0.32, 0.68, 1], [0, peak, peak, 0]);
  const bg = `radial-gradient(ellipse ${size} at ${pos}, ${color}, transparent 70%)`;
  return (
    <motion.div ref={ref} aria-hidden className="pointer-events-none absolute inset-0 -z-10"
      style={reduce ? { background: bg } : { y, opacity, background: bg }} />
  );
}

/* A red hairline that draws itself at a band seam — choreographs the cut. */
function Seam({ light }) {
  return (
    <div aria-hidden className={`relative h-px ${light ? "bg-[#FAF9F7]" : "bg-[#050505]"}`}>
      <motion.span className="absolute left-1/2 top-0 -translate-x-1/2 h-px block"
        style={{ background: `linear-gradient(90deg, transparent, ${RED}, transparent)` }}
        initial={{ width: 0, opacity: 0 }} whileInView={{ width: "70%", opacity: 1 }}
        viewport={{ once: true }} transition={{ duration: 1.2, ease: EASE }} />
    </div>
  );
}

/* ───────── Preloader ───────── */
function Preloader({ done }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let t0 = null, raf;
    const step = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / 1000, 1);
      setN(Math.floor(p * 100));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <motion.div initial={{ y: 0 }} animate={done ? { y: "-100%" } : { y: 0 }}
      transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1], delay: 0.15 }}
      className="fixed inset-0 z-[100] bg-[#050505] flex flex-col items-center justify-center pointer-events-none">
      <span className="text-base tracking-[0.18em] uppercase mb-3 font-bold">
        <span className="text-white">DENTSU </span><span className="text-[#E31937]">AMP</span>
      </span>
      <span className="text-white/30 text-[11px] tracking-[0.3em]" style={mono}>{String(n).padStart(3, "0")} · LOADING THE STUDIO</span>
    </motion.div>
  );
}

/* ───────── Hero wall ───────── */
function WallVideo({ name, delay }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const nudge = () => { if (v.src && v.paused) v.play().catch(() => {}); };
    const t = setTimeout(() => {
      v.src = `${ASSETS}/wall/${name}.mp4`;
      v.play().catch(() => {});
    }, delay);
    const obs = new IntersectionObserver(([en]) => { if (en.isIntersecting) nudge(); }, { threshold: 0.1 });
    obs.observe(v);
    document.addEventListener("visibilitychange", nudge);
    window.addEventListener("pointerdown", nudge, { once: true });
    return () => {
      clearTimeout(t);
      obs.disconnect();
      document.removeEventListener("visibilitychange", nudge);
      window.removeEventListener("pointerdown", nudge);
    };
  }, [name, delay]);
  return (
    <div className="relative rounded-xl overflow-hidden bg-[#0b0b0d] aspect-[9/16] w-full">
      <video ref={ref} muted loop playsInline preload="none" className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-700"
        onPlaying={(e) => { e.currentTarget.style.opacity = 1; }} />
    </div>
  );
}

function HeroWall({ reduce }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -190]);
  const y4 = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const cols = useMemo(() => {
    if (isMobile) return [WALL.slice(0, 2), WALL.slice(2, 4)];
    return [WALL.slice(0, 3), WALL.slice(3, 6), WALL.slice(6, 9), WALL.slice(9, 11).concat(WALL.slice(0, 1))];
  }, [isMobile]);
  const ys = [y1, y2, y3, y4];

  return (
    <section ref={ref} id="top" className="relative min-h-[106vh] overflow-hidden bg-[#050505]">
      <motion.div style={{ opacity: fade }} className="absolute inset-0">
        <div className={`absolute inset-[-12%] grid gap-4 px-6 ${isMobile ? "grid-cols-2" : "grid-cols-4"}`}
          style={{ filter: "blur(4px)", opacity: 0.5 }}>
          {cols.map((col, ci) => (
            <motion.div key={ci} style={{ y: ys[ci] }} initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.25 + ci * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`flex flex-col gap-4 ${ci % 2 === 1 ? "mt-[-9%]" : "mt-[2%]"}`}>
              {col.map((name, i) => <WallVideo key={name + i} name={name} delay={500 + (ci * 3 + i) * 220} />)}
            </motion.div>
          ))}
        </div>
        {/* Flat dark wash so the reels sit far back as ambient texture, not motion. */}
        <div className="absolute inset-0" style={{ background: "rgba(5,5,5,0.5)" }} />
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 90% 65% at 50% 42%, rgba(5,5,5,0.74) 0%, rgba(5,5,5,0.9) 58%, #050505 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.92) 0%, rgba(5,5,5,0.45) 32%, rgba(5,5,5,0.5) 60%, #050505 100%)" }} />
      </motion.div>
      <div className="noise-overlay" />

      {/* Breathing red bloom behind the lockup */}
      <div aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[88vw] h-[72vh] z-[5] pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, rgba(227,25,55,0.22), transparent 62%)", animation: reduce ? "none" : "heroAura 5.5s ease-in-out infinite" }} />

      <div className="relative z-10 min-h-[100vh] flex flex-col items-center justify-center px-6 text-center">
        <motion.span initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.8 }}
          className="text-[#ff4d64] text-[11px] sm:text-xs tracking-[0.4em] uppercase font-medium mb-7" style={mono}>
          10 studios · 150+ films a month · India's largest content studio · Part of the dentsu network
        </motion.span>
        <h1 className="text-white text-[clamp(3.4rem,12vw,10rem)] mb-7" style={{ ...anton, lineHeight: 0.84 }}>
          {["Outcomes.", "As a service"].map((ln, i, arr) => (
            <span key={ln} className="block overflow-hidden pb-[0.06em] -mb-[0.04em]">
              <motion.span className="block"
                initial={reduce ? { opacity: 0 } : { y: "124%" }}
                animate={reduce ? { opacity: 1 } : { y: 0 }}
                transition={{ delay: 1.2 + i * 0.14, duration: 1.05, ease: [0.16, 1, 0.3, 1] }}>
                {ln}{i === arr.length - 1 && <span className="text-[#E31937]">.</span>}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.45, duration: 0.8 }}
          className="text-white/65 text-base sm:text-lg leading-relaxed max-w-2xl mb-9">
          You don't buy content — you buy outcomes. India's largest content studio, with <span className="text-white font-medium">every asset accountable to a number</span> and a team on the hook for the result.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.55, duration: 0.8 }}
          className="flex items-center gap-4 flex-wrap justify-center">
          <a href="#work" className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-[#E31937] text-white text-sm font-semibold overflow-hidden relative">
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" style={{ background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)" }} />
            Watch how we do it <ArrowRight size={15} />
          </a>
        </motion.div>

        {/* Stat bar — the studio at a glance (TCL hero DNA) */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.7, duration: 0.8 }}
          className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-px w-full max-w-3xl bg-white/10 rounded-xl overflow-hidden">
          {[["360+", "Assets live · May"], ["20", "Accounts"], ["32", "In production"], ["60", "In post"]].map(([n, l]) => (
            <div key={l} className="bg-[#0a0a0c] px-4 py-4 text-center">
              <div className="text-white text-2xl sm:text-3xl" style={anton}>{n}</div>
              <div className="text-white/40 text-[10px] tracking-[0.12em] uppercase mt-1" style={mono}>{l}</div>
            </div>
          ))}
        </motion.div>

        {/* 4-discipline row */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.85, duration: 0.8 }}
          className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4 w-full max-w-4xl">
          {PILLARS.map((p) => (
            <div key={p.n} className="text-left border-t border-white/15 pt-3">
              <div className="text-[#ff4d64] text-[11px]" style={mono}>{p.n}</div>
              <div className="text-white text-sm font-semibold mt-1">{p.title}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/* ───────── Black band: the problem (the woes) ───────── */
function ProblemBand() {
  return (
    <section id="problem" className="relative isolate bg-[#080809] text-white px-6 py-24 sm:py-32 border-y border-white/5 overflow-hidden">
      <ParallaxAura color="rgba(227,25,55,0.26)" pos="20% 28%" size="80% 85%" drift={20} />
      <ParallaxAura color="rgba(227,25,55,0.12)" pos="88% 80%" size="55% 60%" drift={26} />
      <div className="relative z-10 max-w-[1280px] mx-auto">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={rise} className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#E31937]" />
            <span className="text-[#ff4d64] text-[11px] tracking-[0.35em] uppercase" style={mono}>01 · Why marketing budgets leak</span>
          </div>
          <h2 className="text-white text-[clamp(2.4rem,6.5vw,5rem)] mb-6" style={{ ...anton, lineHeight: 0.9 }}>
            <RevealLines lines={["Your budget isn't", "the problem.", "The handoffs are"]} dot />
          </h2>
          <p className="text-white/55 text-base sm:text-lg leading-relaxed">
            You're not running one marketing operation — you're running five disconnected ones: brand, performance, production, influencer, media. Every handoff between them leaks ROI. By the time a campaign reaches the market, fragmentation has quietly taxed <span className="text-white">5–6X</span> of its potential.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
          {WOES.map((w, i) => (
            <motion.div key={w.n} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }}
              variants={{ hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } } }}
              className="relative pt-5">
              <motion.span className="absolute top-0 left-0 h-[2px] w-full bg-[#E31937] origin-left"
                variants={{ hidden: { scaleX: 0 }, show: { scaleX: 1, transition: { duration: 0.9, delay: i * 0.08 + 0.15, ease: EASE } } }} />
              <div className="text-[#E31937] text-4xl mb-3" style={anton}>{w.n}</div>
              <h3 className="text-white text-lg mb-2.5" style={anton}>{w.title}</h3>
              <p className="text-white/45 text-sm leading-relaxed">{w.cost}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───────── Full-bleed red band — the screenshot beat ───────── */
function RedBand() {
  return (
    <section className="relative bg-[#E31937] text-[#0A0A0A] px-6 py-20 sm:py-28 overflow-hidden red-grain">
      <div className="max-w-[1100px] mx-auto">
        <h2 className="text-[clamp(2.2rem,6.5vw,5.2rem)]" style={{ ...anton, lineHeight: 0.92, color: "#0A0A0A" }}>
          <RevealLines lines={["It was never a", "creative problem.", "It's a systems problem"]} dot />
        </h2>
      </div>
    </section>
  );
}

/* ───────── White band: the Four Solves (Outcomes as a Service) ───────── */
function Pillars() {
  return (
    <section id="solve" className="relative bg-[#FAF9F7] text-[#0A0A0A] px-6 py-24 sm:py-32">
      <div className="max-w-[1280px] mx-auto">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={rise} className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#E31937]" />
            <span className="text-[#E31937] text-[11px] tracking-[0.35em] uppercase" style={mono}>02 · One operating system</span>
          </div>
          <h2 className="text-[clamp(2.4rem,6vw,4.8rem)]" style={{ ...anton, lineHeight: 0.9 }}>
            <RevealLines lines={["One operating system.", "Not four agencies"]} dot />
          </h2>
          <p className="text-[#0A0A0A]/60 text-base sm:text-lg leading-relaxed max-w-2xl mt-5">
            Strategy, production, media, influence and performance under one P&amp;L — nothing handed off, so nothing leaks. The 5–6X fragmentation tax, gone.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
        {PILLARS.map((p, i) => (
          <motion.div key={p.n} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }}
            variants={{ hidden: { opacity: 0, y: 26 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } } }}
            className="relative pt-5">
            <span className="absolute top-0 left-0 h-[2px] w-full bg-[#0A0A0A]/15" />
            <motion.span className="absolute top-0 left-0 h-[2px] w-full bg-[#E31937] origin-left"
              variants={{ hidden: { scaleX: 0 }, show: { scaleX: 1, transition: { duration: 1.0, delay: i * 0.08 + 0.15, ease: EASE } } }} />
            <div className="flex items-baseline gap-4 mb-3">
              <span className="text-[#E31937] text-5xl" style={anton}>{p.n}</span>
              <h3 className="text-xl leading-tight" style={anton}>{p.title}</h3>
            </div>
            <p className="text-[#0A0A0A]/55 text-[12px] leading-relaxed tracking-[0.04em] uppercase" style={mono}>{p.sub}</p>
          </motion.div>
        ))}
        </div>
      </div>
    </section>
  );
}

/* ───────── Case studies: pain → solve → number ───────── */
function CaseStudies() {
  return (
    <div className="max-w-[1280px] mx-auto px-6 mb-16 grid grid-cols-1 lg:grid-cols-3 gap-5">
      {CASE_STUDIES.map((c, i) => (
        <motion.div key={c.client} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
          variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } } }}
          className="group relative rounded-2xl border border-white/10 bg-[#0c0c0e] p-7 hover:border-[#E31937]/45 transition-colors duration-500">
          <div className="flex items-baseline justify-between gap-3 mb-5">
            <span className="text-white text-2xl" style={anton}>{c.client}</span>
            <span className="text-[#ff6b81] text-[10px] tracking-[0.2em] uppercase text-right" style={mono}>{c.tag}</span>
          </div>
          <div className="space-y-4 mb-6">
            <div>
              <div className="text-[#E31937] text-[10px] tracking-[0.25em] uppercase mb-1.5" style={mono}>The woe</div>
              <p className="text-white/55 text-sm leading-relaxed">{c.woe}</p>
            </div>
            <div>
              <div className="text-[#E31937] text-[10px] tracking-[0.25em] uppercase mb-1.5" style={mono}>The solve</div>
              <p className="text-white/55 text-sm leading-relaxed">{c.solve}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-5 border-t border-white/10">
            {c.metrics.map(([big, small], j) => (
              <div key={j}>
                <div className="text-white text-2xl sm:text-3xl" style={anton}>{big}</div>
                <div className="text-white/40 text-[11px] tracking-[0.1em] uppercase mt-1" style={mono}>{small}</div>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ───────── Outline marquee ───────── */
function OutlineMarquee({ items, reverse, speed = 38, light }) {
  return (
    <div className="relative py-2 overflow-hidden select-none" aria-hidden="true">
      <div style={{ display: "flex", whiteSpace: "nowrap", animation: `ticker ${speed}s linear infinite${reverse ? " reverse" : ""}` }}>
        {[...items, ...items].map((t, i) => (
          <span key={i} className={`mx-6 text-[clamp(2.6rem,7vw,5.5rem)] leading-none ${i % 4 === 1 ? "text-[#E31937]" : light ? "outline-text-dark" : "outline-text"}`} style={anton}>
            {t}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───────── Theater ───────── */
function Theater() {
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start end", "center center"] });
  const scale = useTransform(scrollYProgress, [0, 1], [0.84, 1]);
  const radius = useTransform(scrollYProgress, [0, 1], [28, 8]);

  const play = useCallback(() => {
    const v = vidRef.current;
    if (!v) return;
    if (!v.src) v.src = `${ASSETS}/showreel.mp4`;
    v.muted = false;
    v.controls = true;
    v.play().catch(() => {});
    setPlaying(true);
  }, []);

  return (
    <section id="reel" className="relative px-4 sm:px-6 py-20 sm:py-28 bg-[#050505]">
      <div className="max-w-[1200px] mx-auto mb-10 px-2">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }} variants={rise}>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#E31937]" />
            <span className="text-[#ff4d64] text-[11px] tracking-[0.35em] uppercase" style={mono}>Showreel '26</span>
          </div>
          <h2 className="text-white text-4xl sm:text-6xl" style={anton}>Six months. One reel<span className="text-[#E31937]">.</span></h2>
        </motion.div>
      </div>
      <motion.div ref={wrapRef} style={{ scale, borderRadius: radius }}
        className="relative overflow-hidden bg-[#0a0a0c] aspect-video max-w-[1200px] mx-auto border border-white/10">
        <video ref={vidRef} playsInline preload="none" className="absolute inset-0 w-full h-full object-cover" />
        {!playing && (
          <button onClick={play} className="absolute inset-0 flex flex-col items-center justify-center gap-5 group cursor-pointer"
            style={{ background: "radial-gradient(ellipse at center, rgba(227,25,55,0.12), rgba(5,5,5,0.93) 75%)" }}>
            <span className="relative w-20 h-20 rounded-full bg-[#E31937] flex items-center justify-center transition-transform duration-300 group-hover:scale-110" style={{ boxShadow: "0 0 70px rgba(227,25,55,0.5)" }}>
              <span className="absolute inset-0 rounded-full border border-[#E31937]/60" style={{ animation: "pulseRing 2.4s ease-out infinite" }} />
              <Play size={28} className="text-white ml-1" />
            </span>
            <span className="text-white/55 text-[11px] tracking-[0.3em] uppercase" style={mono}>Play · 1:45 · sound on</span>
          </button>
        )}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[6%] bg-[#050505]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[6%] bg-[#050505]" />
      </motion.div>
    </section>
  );
}

/* ───────── THE UNVEILING — white band + curtain (HUL DNA) ───────── */
function Unveiling() {
  const stageRef = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([en]) => { if (en.isIntersecting) setOpen(true); }, { threshold: 0.35 });
    if (stageRef.current) obs.observe(stageRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="system" className="relative bg-[#FAF9F7] text-[#0A0A0A] px-6 py-24 sm:py-32 overflow-hidden">
      <div className="max-w-[1100px] mx-auto text-center mb-12">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }} variants={rise}>
          <div className="flex items-center justify-center gap-4 mb-5">
            <span className="h-px w-10 bg-[#E31937]" />
            <span className="text-[#E31937] text-[11px] tracking-[0.45em] uppercase" style={mono}>04 · Dream Machine</span>
            <span className="h-px w-10 bg-[#E31937]" />
          </div>
          <h2 className="text-[clamp(2.6rem,7vw,5.5rem)]" style={{ ...anton, lineHeight: 0.88 }}>
            <RevealLines lines={["The engine", "behind it"]} dot />
          </h2>
          <p className="text-[#0A0A0A]/60 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mt-6">
            We built Dream Machine — the operating system our studios run on, and the one thing your incumbent agency can't copy. You get a live dashboard, not a status email: every asset, its performance score, and what it cost. Briefs in, performant assets out, learning back into the next brief.
          </p>
        </motion.div>
      </div>

      {/* Curtain stage */}
      <div ref={stageRef} className="relative max-w-[1100px] mx-auto rounded-2xl overflow-hidden" style={{ boxShadow: "0 40px 110px rgba(10,10,10,0.18)" }}>
        <div className="rounded-2xl overflow-hidden border border-[#0A0A0A]/10 bg-[#0A0A0A]">
          <div className="flex items-center gap-1.5 px-4 py-2.5 bg-[#101012] border-b border-white/5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#E31937]/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            <span className="ml-3 text-white/30 text-[10px] tracking-[0.2em]" style={mono}>DREAM MACHINE · LIVE OS</span>
            <span className="ml-auto flex items-center gap-1.5 text-[#ff6b81] text-[10px] tracking-[0.2em]" style={mono}>
              <span className="w-1.5 h-1.5 rounded-full bg-[#E31937]" style={{ animation: open ? "livePulse 1.8s ease-in-out infinite" : "none" }} /> LIVE
            </span>
          </div>
          <div className="relative">
            <img src={`${ASSETS}/dream-machine.jpg`} alt="Dream Machine — dentsuAMP's operating system" loading="lazy" className="w-full block" />
            {/* scan sweep — makes the still feel like a running screen */}
            {open && <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 0%, rgba(227,25,55,0.10) 50%, transparent 100%)", height: "30%", animation: "scanY 4.5s ease-in-out infinite" }} />}
          </div>
        </div>
        {/* Curtains */}
        <div className={`absolute inset-y-0 left-0 w-1/2 bg-[#0A0A0A] transition-transform duration-[1400ms] ${open ? "-translate-x-full" : ""}`}
          style={{ transitionTimingFunction: "cubic-bezier(0.76, 0, 0.24, 1)", borderRight: `2px solid ${RED}` }} />
        <div className={`absolute inset-y-0 right-0 w-1/2 bg-[#0A0A0A] transition-transform duration-[1400ms] ${open ? "translate-x-full" : ""}`}
          style={{ transitionTimingFunction: "cubic-bezier(0.76, 0, 0.24, 1)", borderLeft: `2px solid ${RED}` }} />
        {!open && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-white/60 text-[11px] tracking-[0.4em] uppercase" style={mono}>— Scroll to unveil —</span>
          </div>
        )}
      </div>

      {/* OS features strip */}
      <div className="max-w-[1100px] mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
        {OS_FEATURES.map(([k, v], i) => (
          <motion.div key={k} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }}
            variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } } }}
            className="border-t-2 border-[#0A0A0A] pt-4">
            <div className="text-[#E31937] text-[12px] tracking-[0.25em] mb-1.5" style={mono}>{k}</div>
            <div className="text-[#0A0A0A]/60 text-sm leading-relaxed">{v}</div>
          </motion.div>
        ))}
      </div>

      {/* Contextual CTA at peak belief */}
      <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.6 }} variants={rise}
        className="max-w-[1100px] mx-auto mt-12 text-center">
        <button onClick={() => openEnquiry({ kind: "walkthrough", prefill: "I'd like to see Dream Machine run on our brand. What we're trying to grow:" })}
          className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold hover:bg-[#E31937] transition-colors">
          See Dream Machine run on your brand <ArrowRight size={15} />
        </button>
      </motion.div>
    </section>
  );
}

/* ───────── Work rows ───────── */
function RowCard({ reel }) {
  const ref = useRef(null);
  const vidRef = useRef(null);
  const [sound, setSound] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([en]) => {
      const v = vidRef.current;
      if (!v) return;
      if (en.isIntersecting) {
        if (!v.src) v.src = `${ASSETS}/reels/${reel.file}`;
        v.play().catch(() => {});
      } else { v.pause(); }
    }, { threshold: 0.3 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [reel.file]);
  const toggleSound = () => {
    const v = vidRef.current;
    if (!v) return;
    v.muted = sound;
    setSound(!sound);
  };
  return (
    <div ref={ref} className="group relative flex-none w-[225px] sm:w-[255px] snap-start">
      <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#0c0c0e] aspect-[9/16] transition-all duration-500 group-hover:-translate-y-2 group-hover:border-[#E31937]/50 group-hover:shadow-[0_24px_60px_-12px_rgba(227,25,55,0.35)]">
        <video ref={vidRef} muted loop playsInline preload="none" className="absolute inset-0 w-full h-full object-cover" />
        <button onClick={toggleSound} aria-label="Toggle sound"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/55 backdrop-blur flex items-center justify-center text-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
          {sound ? <Volume2 size={15} /> : <VolumeX size={15} />}
        </button>
        <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/90 to-transparent">
          <div className="text-[10px] text-[#ff6b81] tracking-[0.25em] mb-1" style={mono}>{reel.code} · {reel.client.toUpperCase()}</div>
          <div className="text-white text-sm font-semibold">{reel.title}</div>
        </div>
      </div>
    </div>
  );
}

function WorkRow({ label, sub, reels }) {
  return (
    <div className="mb-14">
      <div className="max-w-[1280px] mx-auto px-6 flex items-baseline justify-between mb-5">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.6 }} variants={rise}>
          <span className="text-white text-2xl sm:text-3xl" style={anton}>{label}</span>
          <span className="hidden sm:inline text-white/30 text-xs ml-4" style={mono}>{sub}</span>
        </motion.div>
        <span className="text-white/25 text-[11px] tracking-[0.2em] uppercase" style={mono}>Swipe →</span>
      </div>
      <div className="relative">
        <div className="flex gap-5 overflow-x-auto px-6 pb-3 snap-x snap-mandatory max-w-[1280px] mx-auto hide-scroll">
          {reels.map((r) => <RowCard key={r.code} reel={r} />)}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-[#050505] to-transparent hidden sm:block" />
      </div>
    </div>
  );
}

/* ───────── Stats ───────── */
function StatCell({ stat, animate }) {
  const count = useCountUp(stat.value, 1800, animate);
  return (
    <div className="text-center px-4 py-7">
      <div className="text-white text-4xl sm:text-6xl mb-2" style={anton}>
        {stat.prefix || ""}{animate ? count : 0}<span className="text-[#E31937]">{stat.suffix}</span>
      </div>
      <div className="text-white/40 text-[11px] tracking-[0.25em] uppercase" style={mono}>{stat.label}</div>
    </div>
  );
}

/* ───────── Commercials: fixed tiers + retainer configurator ─────────
 * All rupee figures live here — edit freely. Rates are per month unless noted. */
const PRICING = {
  oneTime: 500000,     // ₹5L platform onboarding (one-time)
  brandBase: 250000,   // ₹2.5L / brand / mo — strategy, planning, AM, reporting
  videoRate: 75000,    // ₹75k / hero video
  staticRate: 4500,    // ₹4.5k / static
  creatorRate: 45000,  // ₹45k / creator collab
  mediaMgmt: 150000,   // ₹1.5L / mo — performance media management
};
// Volume discount on the variable (production) spend, by total monthly asset count.
const VOLUME_BREAKS = [
  { min: 240, off: 0.27 },
  { min: 120, off: 0.18 },
  { min: 60, off: 0.10 },
  { min: 0, off: 0 },
];
const LEVERS = [
  { k: "brands", label: "Brands / markets", min: 1, max: 10, step: 1 },
  { k: "videos", label: "Hero videos / month", min: 4, max: 96, step: 4 },
  { k: "statics", label: "Statics / month", min: 0, max: 200, step: 10 },
  { k: "creators", label: "Creator collabs / month", min: 0, max: 24, step: 2 },
];
const TIERS = [
  {
    name: "Ignite", price: "₹6L", per: "/month", tagline: "The pilot · one brand",
    assets: "~40 assets / month",
    team: ["1 strategist", "2 content creators", "1 editor", "1 account manager"],
    results: ["See the studio before a retainer", "Always-on social, weekly read-out", "48-hour turnarounds · no annual lock-in"],
    highlight: false,
  },
  {
    name: "Velocity", price: "₹15L", per: "/month", tagline: "Full-funnel growth",
    assets: "~100 assets / month",
    team: ["Dedicated strategist", "4 creators", "2 editors", "1 producer", "1 performance lead", "1 account manager"],
    results: ["TOFU · MOFU · BOFU coverage", "CTR · ROAS · CPA accountable", "Creator-led content engine", "Weekly optimisation cycle"],
    highlight: true,
  },
  {
    name: "Scale", price: "₹30L+", per: "/month", tagline: "Multi-brand · dedicated studio",
    assets: "200+ assets / month",
    team: ["Multi-pod team", "Dedicated studio capacity", "Creator engine", "Media buying desk", "Senior strategy lead"],
    results: ["Named SLAs", "Dedicated studio days", "Full media management", "Monthly business reviews"],
    highlight: false,
  },
];

const fmtMoney = (n) => (n >= 10000000 ? `₹${(n / 10000000).toFixed(2)} Cr` : `₹${(n / 100000).toFixed(1)} L`);
const fmtRupee = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

function teamFor({ brands, videos, statics, creators, media }) {
  return [
    { role: "Strategists", count: Math.max(1, Math.ceil(brands / 2)) },
    { role: "Creators / shooters", count: Math.max(1, Math.ceil(videos / 12)) },
    { role: "Editors", count: Math.max(1, Math.ceil(videos / 16)) },
    { role: "Designers", count: Math.max(1, Math.ceil(statics / 40)) },
    { role: "Creator managers", count: creators > 0 ? Math.max(1, Math.ceil(creators / 12)) : 0 },
    { role: "Performance leads", count: media ? 1 : 0 },
    { role: "Producers", count: 1 },
    { role: "Account managers", count: Math.max(1, Math.ceil(brands / 2)) },
  ].filter((x) => x.count > 0);
}

function Configurator() {
  const [s, setS] = useState({ brands: 2, videos: 24, statics: 60, creators: 6, media: true });
  const set = (k) => (e) => setS((p) => ({ ...p, [k]: k === "media" ? e.target.checked : +e.target.value }));

  const assetCount = s.videos + s.statics + s.creators;
  const off = VOLUME_BREAKS.find((b) => assetCount >= b.min).off;
  const variable = s.videos * PRICING.videoRate + s.statics * PRICING.staticRate + s.creators * PRICING.creatorRate;
  const monthly = s.brands * PRICING.brandBase + variable * (1 - off) + (s.media ? PRICING.mediaMgmt : 0);
  const perAsset = assetCount ? monthly / assetCount : 0;
  const team = teamFor(s);
  const teamTotal = team.reduce((a, t) => a + t.count, 0);

  // Tween the headline retainer figure so dragging feels fluid, not steppy.
  const monthlySpring = useSpring(monthly, { stiffness: 90, damping: 18, mass: 0.4 });
  const [monthlyDisp, setMonthlyDisp] = useState(monthly);
  useEffect(() => { monthlySpring.set(monthly); }, [monthly, monthlySpring]);
  useMotionValueEvent(monthlySpring, "change", (v) => setMonthlyDisp(v));

  const lockScope = () => {
    const scope = {
      brands: s.brands, videos: s.videos, statics: s.statics, creators: s.creators,
      media: s.media ? "Yes" : "No", monthly: fmtMoney(monthly), perAsset: fmtRupee(perAsset),
      assetCount, teamTotal, off: off > 0 ? Math.round(off * 100) : 0,
    };
    const prefill =
      `My retainer scope: ${s.brands} brand(s), ${s.videos} videos, ${s.statics} statics, ${s.creators} creator collabs/mo, ` +
      `media management ${s.media ? "on" : "off"} → ${fmtMoney(monthly)}/mo (${assetCount} assets, ${teamTotal}-person team). What we're trying to grow:`;
    openEnquiry({ kind: "retainer", scope, prefill });
  };

  return (
    <div className="rounded-2xl bg-[#0A0A0A] text-white p-7 sm:p-10">
      <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
        {/* Levers */}
        <div>
          <div className="text-[#ff4d64] text-[11px] tracking-[0.3em] uppercase mb-2" style={mono}>Build your retainer</div>
          <h3 className="text-3xl sm:text-4xl mb-8" style={anton}>Your scope. Your price<span className="text-[#E31937]">.</span></h3>
          {LEVERS.map((l) => (
            <div key={l.k} className="mb-6">
              <div className="flex items-baseline justify-between mb-2.5">
                <span className="text-sm text-white/65">{l.label}</span>
                <span className="text-xl text-white" style={anton}>{s[l.k]}</span>
              </div>
              <input type="range" className="amp-range w-full" min={l.min} max={l.max} step={l.step} value={s[l.k]} onChange={set(l.k)} />
            </div>
          ))}
          <label className="flex items-center justify-between py-4 border-t border-white/10 cursor-pointer mt-2">
            <span className="text-sm text-white/65">Performance media management</span>
            <span className={`relative w-11 h-6 rounded-full transition-colors duration-300 ${s.media ? "bg-[#E31937]" : "bg-white/15"}`}>
              <input type="checkbox" className="sr-only" checked={s.media} onChange={set("media")} />
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform duration-300 ${s.media ? "translate-x-5" : ""}`} />
            </span>
          </label>
        </div>

        {/* Readout */}
        <div className="rounded-xl bg-[#111113] border border-white/10 p-6 sm:p-7 flex flex-col">
          <div className="text-white/40 text-[11px] tracking-[0.25em] uppercase mb-2" style={mono}>Estimated monthly retainer</div>
          <div className="flex items-center gap-3 flex-wrap mb-1.5">
            <span className="text-5xl sm:text-6xl text-white tabular-nums" style={anton}>{fmtMoney(monthlyDisp)}</span>
            {off > 0 && <span className="px-2.5 py-1 rounded-full bg-[#E31937]/15 text-[#ff4d64] text-[11px] font-semibold tracking-wide">−{Math.round(off * 100)}% volume</span>}
          </div>
          <div className="text-white/45 text-sm">≈ {fmtRupee(perAsset)} per asset · {assetCount} assets / month</div>
          <div className="text-white/30 text-[12px] mt-1.5 mb-6">+ {fmtMoney(PRICING.oneTime)} one-time platform onboarding</div>

          <div className="text-white/40 text-[11px] tracking-[0.25em] uppercase mb-3" style={mono}>The team you get · {teamTotal} people</div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-2 mb-7">
            {team.map((t) => (
              <div key={t.role} className="flex items-baseline justify-between border-b border-white/5 pb-1.5">
                <span className="text-white/60 text-[13px]">{t.role}</span>
                <span className="text-white text-base" style={anton}>{t.count}</span>
              </div>
            ))}
          </div>
          <button onClick={lockScope} className="group relative mt-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E31937] text-white text-sm font-semibold overflow-hidden">
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" style={{ background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)" }} />
            Lock this scope <ArrowRight size={15} />
          </button>
          <p className="text-white/30 text-[11px] text-center mt-3">Indicative — a strategist confirms the scope on a 30-min call within one business day.</p>
        </div>
      </div>
    </div>
  );
}

function Commercials() {
  return (
    <section id="pricing" className="relative bg-[#FAF9F7] text-[#0A0A0A] px-6 py-24 sm:py-32">
      <div className="max-w-[1280px] mx-auto">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={rise} className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-[#E31937]" />
            <span className="text-[#E31937] text-[11px] tracking-[0.35em] uppercase" style={mono}>06 · The commercials</span>
          </div>
          <h2 className="text-[clamp(2.4rem,6vw,4.8rem)] mb-5" style={{ ...anton, lineHeight: 0.9 }}>
            <RevealLines lines={["No retainers", "in the dark"]} dot />
          </h2>
          <p className="text-[#0A0A0A]/60 text-base sm:text-lg leading-relaxed">
            Pick a productized tier or build a retainer around your exact volume. Either way you see the fixed fee, the exact team you get, and the results you can hold us to — before you sign.
          </p>
        </motion.div>

        {/* Fixed-fee tiers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-16">
          {TIERS.map((t, i) => (
            <motion.div key={t.name} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }}
              variants={{ hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } } }}
              className={`relative rounded-2xl border p-7 flex flex-col transition-transform duration-500 hover:-translate-y-1.5 ${t.highlight ? "amp-pop border-[#E31937] bg-[#0A0A0A] text-white" : "border-[#0A0A0A]/12 bg-white hover:border-[#E31937]/40"}`}>
              {t.highlight && <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-[#E31937] text-white text-[10px] tracking-[0.2em] uppercase" style={mono}>Most popular</span>}
              <div className={`text-[11px] tracking-[0.25em] uppercase mb-4 ${t.highlight ? "text-white/55" : "text-[#0A0A0A]/45"}`} style={mono}>{t.tagline}</div>
              <div className="text-3xl mb-3" style={anton}>AMP {t.name}</div>
              <div className="flex items-baseline gap-1.5 mb-1">
                <span className="text-5xl" style={anton}>{t.price}</span>
                <span className={`text-sm ${t.highlight ? "text-white/50" : "text-[#0A0A0A]/45"}`}>{t.per}</span>
              </div>
              <div className={`text-sm mb-6 ${t.highlight ? "text-[#ff6b81]" : "text-[#E31937]"}`}>{t.assets}</div>

              <div className={`text-[10px] tracking-[0.25em] uppercase mb-2.5 ${t.highlight ? "text-white/40" : "text-[#0A0A0A]/40"}`} style={mono}>The team</div>
              <ul className="mb-5 space-y-1.5">
                {t.team.map((m) => (
                  <li key={m} className={`flex items-start gap-2 text-sm ${t.highlight ? "text-white/75" : "text-[#0A0A0A]/70"}`}>
                    <span className="text-[#E31937] mt-0.5">›</span>{m}
                  </li>
                ))}
              </ul>
              <div className={`text-[10px] tracking-[0.25em] uppercase mb-2.5 ${t.highlight ? "text-white/40" : "text-[#0A0A0A]/40"}`} style={mono}>What you get</div>
              <ul className="mb-7 space-y-1.5">
                {t.results.map((m) => (
                  <li key={m} className={`flex items-start gap-2 text-sm ${t.highlight ? "text-white/75" : "text-[#0A0A0A]/70"}`}>
                    <span className="text-[#E31937] mt-0.5">✓</span>{m}
                  </li>
                ))}
              </ul>
              <button onClick={() => openEnquiry({ kind: "tier", tier: t.name, prefill: `I'm interested in AMP ${t.name} (${t.price}${t.per}). Here's what we're trying to grow:` })}
                className={`mt-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-colors ${t.highlight ? "bg-[#E31937] text-white hover:bg-[#c9142e]" : "border border-[#0A0A0A]/20 text-[#0A0A0A] hover:border-[#E31937] hover:text-[#E31937]"}`}>
                {t.name === "Ignite" ? "Start the pilot" : "Enquire"} <ArrowRight size={14} />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Custom retainer configurator */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={rise}>
          <Configurator />
        </motion.div>

        {/* Risk reversal */}
        <motion.p initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.6 }} variants={rise}
          className="text-center text-[#0A0A0A]/55 text-sm mt-8 max-w-2xl mx-auto">
          <span className="text-[#0A0A0A] font-medium">Month one is a paid proof sprint</span> — a defined outcome, or we course-correct. No annual lock-in. Start on Ignite and move up when the numbers show.
        </motion.p>
      </div>
    </section>
  );
}

/* ───────── The offer — Proof Sprint (replaces the public pricing block).
   Low-risk, full-price: one month, one outcome, guarantee. No scarcity. ───── */
function OfferBand() {
  return (
    <section id="offer" className="relative bg-[#E31937] text-[#0A0A0A] px-6 py-24 sm:py-32 overflow-hidden red-grain">
      <div className="max-w-[1000px] mx-auto text-center">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={{ show: { transition: { staggerChildren: 0.08 } } }}>
          <motion.div variants={rise} className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-8 bg-black/40" />
            <span className="text-black/60 text-[11px] tracking-[0.35em] uppercase" style={mono}>The offer</span>
            <span className="h-px w-8 bg-black/40" />
          </motion.div>
          <motion.h2 variants={rise} className="text-[clamp(2.6rem,7vw,5.5rem)] mb-6" style={{ ...anton, lineHeight: 0.9 }}>
            <RevealLines lines={["The proof sprint"]} />
          </motion.h2>
          <motion.p variants={rise} className="text-black/75 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto mb-4 font-medium">
            One month. One outcome you define. A fixed fee, no annual lock-in.
          </motion.p>
          <motion.p variants={rise} className="text-black text-xl sm:text-2xl leading-snug max-w-2xl mx-auto mb-10" style={{ fontWeight: 700 }}>
            We hit the agreed benchmark — or the next sprint's on us.
          </motion.p>
          <motion.div variants={rise} className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 mb-10 text-black/70 text-sm" style={mono}>
            <span>○ Defined outcome</span>
            <span>○ No annual lock-in</span>
            <span>○ Full team from day one</span>
          </motion.div>
          <motion.div variants={rise}>
            <button onClick={() => openEnquiry({ kind: "general", prefill: "We'd like a proof sprint. The outcome we want to hit:" })}
              className="group relative inline-flex items-center gap-2.5 px-9 py-4 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold overflow-hidden">
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" style={{ background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.25) 50%, transparent 65%)" }} />
              Book a proof sprint <ArrowRight size={15} />
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

/* ───────── Proof voices — testimonial + who runs your account ───────── */
function ProofVoices() {
  return (
    <div className="max-w-[1280px] mx-auto px-6 mb-16">
      <motion.figure initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.35 }} variants={rise}
        className="relative rounded-2xl border border-white/10 bg-[#0c0c0e] p-8 sm:p-10 overflow-hidden">
        <ParallaxAura color="rgba(227,25,55,0.10)" pos="90% 10%" size="50% 80%" drift={10} />
        <div className="relative z-10">
          <div className="text-[#E31937] text-5xl leading-none mb-3" style={anton}>“</div>
          <blockquote className="text-white text-xl sm:text-2xl leading-snug max-w-3xl">{TESTIMONIAL.quote}</blockquote>
          <figcaption className="mt-6 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="w-9 h-9 rounded-full bg-[#E31937]/15 text-[#ff6b81] flex items-center justify-center text-[12px]">★</span>
            <span className="text-white font-medium">{TESTIMONIAL.name}</span>
            <span className="text-white/40">{TESTIMONIAL.role} · {TESTIMONIAL.brand}</span>
          </figcaption>
        </div>
      </motion.figure>
      <div className="text-white/30 text-[11px] tracking-[0.25em] uppercase mt-8 mb-3" style={mono}>Who runs your account</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {POD.map((p) => (
          <div key={p.name} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0a0a0c] px-4 py-3">
            <span className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/70 text-[12px]" style={mono}>{p.initials}</span>
            <div>
              <div className="text-white text-sm">{p.name}</div>
              <div className="text-white/40 text-[12px]">{p.line}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────── Lead capture — real form, posts to the backend ─────────
 * Any CTA fires openEnquiry({kind, ...}); one mounted LeadModal listens. */
function openEnquiry(detail) {
  window.dispatchEvent(new CustomEvent("amp:enquiry", { detail: detail || {} }));
}

const FALLBACK_MAIL = "hello@dentsuamp.com";

function LeadModal() {
  const [ctx, setCtx] = useState(null); // null = closed
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "", company_url: "" });
  const [state, setState] = useState("idle"); // idle | sending | done | error

  useEffect(() => {
    const onOpen = (e) => {
      const d = e.detail || {};
      setCtx(d);
      setState("idle");
      setForm({ name: "", email: "", company: "", message: d.prefill || "", company_url: "" });
    };
    window.addEventListener("amp:enquiry", onOpen);
    return () => window.removeEventListener("amp:enquiry", onOpen);
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setCtx(null); };
    if (ctx) { document.addEventListener("keydown", onKey); document.body.style.overflow = "hidden"; }
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [ctx]);

  if (!ctx) return null;

  const title =
    ctx.kind === "retainer" ? "Lock your scope" :
    ctx.kind === "tier" ? `Start with AMP ${ctx.tier}` :
    ctx.kind === "walkthrough" ? "Book a Dream Machine walkthrough" :
    "Start a project";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || state === "sending") return;
    setState("sending");
    try {
      const res = await fetch("/api/public-enquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, kind: ctx.kind || "general", tier: ctx.tier || "", scope: ctx.scope || {} }),
      });
      if (!res.ok) throw new Error("bad");
      setState("done");
    } catch { setState("error"); }
  };

  const close = () => setCtx(null);
  const field = "w-full bg-[#141416] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#E31937] focus:outline-none transition-colors";

  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={close} />
      <motion.div initial={{ y: 48, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.4, ease: EASE }}
        className="relative w-full sm:max-w-md bg-[#0c0c0e] border border-white/10 rounded-t-2xl sm:rounded-2xl p-7 sm:p-8 text-white max-h-[92vh] overflow-y-auto hide-scroll">
        <button onClick={close} aria-label="Close" className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60">✕</button>

        {state === "done" ? (
          <div className="text-center py-4">
            <div className="w-12 h-12 rounded-full bg-[#E31937]/15 text-[#ff4d64] flex items-center justify-center mx-auto mb-5 text-xl">✓</div>
            <h3 className="text-2xl mb-3" style={anton}>You're in. We're on it<span className="text-[#E31937]">.</span></h3>
            {ctx.kind === "retainer" && ctx.scope && (
              <p className="text-white/45 text-[13px] mb-3">Scope received: {ctx.scope.assetCount} assets/mo · {ctx.scope.monthly} · {ctx.scope.teamTotal}-person team.</p>
            )}
            <p className="text-white/65 text-sm leading-relaxed">A strategist replies within <span className="text-white">one business day</span> with a tailored outcome model — not a pitch.</p>
          </div>
        ) : (
          <>
            <div className="text-[#ff4d64] text-[10px] tracking-[0.3em] uppercase mb-2" style={mono}>{ctx.kind === "walkthrough" ? "Walkthrough" : "Enquiry"}</div>
            <h3 className="text-2xl sm:text-3xl mb-1.5" style={anton}>{title}<span className="text-[#E31937]">.</span></h3>
            <p className="text-white/50 text-[13px] mb-5">A strategist replies within one business day — a tailored outcome model, not a sales pitch.</p>
            {ctx.kind === "retainer" && ctx.scope && (
              <div className="rounded-lg bg-[#141416] border border-white/10 px-4 py-3 mb-5 text-[12px] text-white/55">
                <span className="text-white">{ctx.scope.monthly}/mo</span> · {ctx.scope.assetCount} assets/mo · {ctx.scope.teamTotal}-person team{ctx.scope.off ? ` · ${ctx.scope.off}% volume` : ""}
              </div>
            )}
            <form onSubmit={submit} className="space-y-3">
              <input className={field} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input className={field} type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <input className={field} placeholder="Brand / company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
              <textarea className={field + " resize-none"} rows={3} placeholder="What are you trying to grow?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              {/* honeypot */}
              <input tabIndex={-1} autoComplete="off" className="hidden" value={form.company_url} onChange={(e) => setForm({ ...form, company_url: e.target.value })} />
              <button type="submit" disabled={state === "sending"}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E31937] text-white text-sm font-semibold disabled:opacity-60">
                {state === "sending" ? "Sending…" : "Send enquiry"} <ArrowRight size={15} />
              </button>
              {state === "error" && (
                <p className="text-white/50 text-[12px] text-center">Couldn't send just now — email us at <a className="text-[#ff6b81] underline" href={`mailto:${FALLBACK_MAIL}`}>{FALLBACK_MAIL}</a>.</p>
              )}
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}

/* ───────── Page ───────── */
export default function MarketingPage() {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  // The loading veil plays once per browser session — returning visitors
  // (and in-session reloads) go straight to the wall.
  const [veilSkipped] = useState(() => {
    try {
      if (new URLSearchParams(window.location.search).has("still")) return true;
      if (localStorage.getItem("amp_still") === "1") return true; // QA/capture hatch — never set by app code
      return sessionStorage.getItem("amp_veil") === "1";
    } catch { return false; }
  });
  const [veilDone, setVeilDone] = useState(veilSkipped);
  const [scrolled, setScrolled] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [active, setActive] = useState("top");
  const statsRef = useRef(null);

  // Scroll-progress spine — a sprung red meter at the very top.
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.3 });

  useEffect(() => {
    if (veilSkipped) return;
    const t = setTimeout(() => {
      setVeilDone(true);
      try { sessionStorage.setItem("amp_veil", "1"); } catch {}
    }, 1050);
    return () => clearTimeout(t);
  }, [veilSkipped]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Scroll-spy — which band owns the upper third of the viewport.
  useEffect(() => {
    const ids = ["top", "problem", "solve", "work", "services", "contact"];
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-30% 0px -65% 0px" }
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [veilDone]);

  // Deep-link support: /#system, /#work etc. scroll to the band on a fresh load.
  useEffect(() => {
    if (!veilDone) return;
    let id = window.location.hash.slice(1);
    try { id = id || localStorage.getItem("amp_jump") || ""; } catch {}
    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView();
    const t = setTimeout(() => el.scrollIntoView(), 350);
    return () => clearTimeout(t);
  }, [veilDone]);

  useEffect(() => {
    const obs = new IntersectionObserver(([en]) => { if (en.isIntersecting) setStatsVisible(true); }, { threshold: 0.3 });
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white relative" data-testid="marketing-page">
      {!veilSkipped && <Preloader done={veilDone} />}

      {/* Scroll-progress spine */}
      <motion.div className="fixed top-0 left-0 right-0 h-[2px] z-[60] origin-left bg-[#E31937]" style={{ scaleX: progress }} />

      {/* Lead-capture modal (listens for openEnquiry events) */}
      <LeadModal />

      {/* Nav */}
      <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#050505]/85 backdrop-blur-md border-b border-white/10" : ""}`}>
        <div className="max-w-[1280px] mx-auto flex items-center justify-between px-6 py-4">
          <a href="#top" className="text-[15px] tracking-[0.14em] uppercase font-bold">
            <span className="text-white">DENTSU </span><span className="text-[#E31937]">AMP</span>
          </a>
          <nav className="hidden md:flex items-center gap-1 text-[11px] tracking-[0.2em] uppercase" style={mono}>
            {[["problem", "The Problem"], ["solve", "The Studio"], ["work", "The Proof"], ["services", "Services"], ["contact", "Contact"]].map(([id, label]) => {
              const isActive = active === id;
              return (
                <a key={id} href={`#${id}`} className="relative px-3 py-1.5 transition-colors duration-300"
                  style={{ color: isActive ? "#fff" : "rgba(255,255,255,0.55)" }}>
                  {label}
                  {isActive && <motion.span layoutId="nav-active" className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#E31937]"
                    transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                </a>
              );
            })}
          </nav>
          <button onClick={() => navigate("/login")} className="text-[11px] tracking-[0.2em] uppercase text-white/50 hover:text-[#E31937] transition-colors" style={mono}>
            Team Login →
          </button>
        </div>
      </header>

      {/* Wall hero (black) */}
      <HeroWall reduce={reduce} />

      {/* 01 · The problem — the woes (black) */}
      <ProblemBand />

      {/* Full-bleed red beat (red) */}
      <RedBand />

      {/* Horizontal showreel — sits between the red beat and the white studio */}
      <Theater />

      {/* 02 · The studio / the solve — one operating system (white) */}
      <Pillars />

      {/* 03 · The Proof — "the engine is already running" + assets wall + trusted-by */}
      <section id="work" className="relative isolate overflow-hidden py-20 sm:py-28 bg-[#050505]">
        <ParallaxAura color="rgba(227,25,55,0.20)" pos="80% 20%" size="70% 70%" drift={18} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 mb-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }} variants={rise}>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#E31937]" />
              <span className="text-[#ff4d64] text-[11px] tracking-[0.35em] uppercase" style={mono}>03 · The proof</span>
            </div>
            <h2 className="text-white text-4xl sm:text-6xl mb-5" style={anton}><RevealLines lines={["The engine is", "already running"]} dot /></h2>
            <p className="text-white/60 text-base sm:text-lg leading-relaxed max-w-2xl">
              Over <span className="text-white font-medium">500 videos produced in-house</span> every month · 100k+ reels studied every month. Top, middle and bottom-funnel creative ownership, backed with data science. We're not pitching capability — we're shipping it, today.
            </p>
            <div className="grid grid-cols-3 gap-4 mt-8 max-w-2xl">
              {[["500+", "Videos / month"], ["5,000+", "Creator assets / month"], ["10+", "Owned studios"]].map(([n, l]) => (
                <div key={l} className="border-t-2 border-[#E31937]/60 pt-3">
                  <div className="text-white text-3xl sm:text-4xl" style={anton}>{n}</div>
                  <div className="text-white/45 text-[11px] tracking-[0.15em] uppercase mt-1" style={mono}>{l}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
        <WorkRow label="PERFORMANCE FILMS" sub="CTR-accountable creative · PhonePe" reels={ROW_PERFORMANCE} />
        <WorkRow label="BRAND ENGINES" sub="Always-on social · OWND! · dentsuAMP" reels={ROW_BRAND} />
        <div className="mt-10">
          <div className="text-center mb-6"><span className="text-white/30 text-[11px] tracking-[0.3em] uppercase" style={mono}>Trusted by</span></div>
          <OutlineMarquee items={CLIENTS.slice(0, 6)} speed={30} />
          <OutlineMarquee items={CLIENTS.slice(6)} reverse speed={34} />
        </div>
      </section>

      {/* 04 · Empirical evidence — case studies (across brands) */}
      <section className="relative isolate overflow-hidden py-20 sm:py-28 bg-[#070708]">
        <ParallaxAura color="rgba(227,25,55,0.14)" pos="50% 30%" size="80% 90%" drift={14} />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 mb-10">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }} variants={rise}>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#E31937]" />
              <span className="text-[#ff4d64] text-[11px] tracking-[0.35em] uppercase" style={mono}>04 · Empirical evidence</span>
            </div>
            <h2 className="text-white text-4xl sm:text-6xl" style={anton}><RevealLines lines={["The work is the argument"]} dot /></h2>
          </motion.div>
        </div>
        <CaseStudies />
        <ProofVoices />
      </section>

      {/* 07 · Services (white) */}
      <section id="services" className="relative bg-[#FAF9F7] text-[#0A0A0A] px-6 py-24 sm:py-32">
        <div className="max-w-[1100px] mx-auto">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={rise}>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-[#E31937]" />
              <span className="text-[#E31937] text-[11px] tracking-[0.35em] uppercase" style={mono}>05 · What we do</span>
            </div>
            <h2 className="text-[clamp(2.4rem,6vw,4.6rem)] mb-12" style={{ ...anton, lineHeight: 0.9 }}>
              <RevealLines lines={["What we do"]} dot />
            </h2>
          </motion.div>
          <div>
            {SERVICES.map((s) => (
              <motion.div key={s.n} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.5 }} variants={rise}
                className="group relative flex items-baseline gap-6 sm:gap-10 py-6 border-b border-[#0A0A0A]/10 overflow-hidden">
                <span className="absolute bottom-0 left-0 h-px w-0 bg-gradient-to-r from-[#E31937] to-transparent transition-all duration-700 group-hover:w-full" />
                <span className="text-[#E31937] text-sm flex-none" style={mono}>{s.n}</span>
                <div className="flex-1 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6">
                  <span className="text-3xl sm:text-5xl text-[#0A0A0A]/85 group-hover:text-[#0A0A0A] transition-all duration-300 group-hover:translate-x-2" style={anton}>{s.name}</span>
                  <span className="text-[#0A0A0A]/45 text-sm sm:text-right max-w-sm">{s.desc}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 06 · Contact (black) */}
      <section id="contact" className="relative px-6 py-28 sm:py-40 overflow-hidden bg-[#050505]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none w-[900px] h-[600px]"
          style={{ background: "radial-gradient(ellipse, rgba(227,25,55,0.24) 0%, transparent 68%)", animation: "heroAura 5s ease-in-out infinite" }} />
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={{ show: { transition: { staggerChildren: 0.1 } } }}
          className="relative max-w-[900px] mx-auto text-center">
          <motion.div variants={rise} className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-8 bg-[#E31937]" />
            <span className="text-[#ff4d64] text-[11px] tracking-[0.35em] uppercase" style={mono}>08 · Start</span>
            <span className="h-px w-8 bg-[#E31937]" />
          </motion.div>
          <motion.h2 variants={rise} className="text-white text-[clamp(2.6rem,8vw,6.5rem)] mb-7" style={{ ...anton, lineHeight: 0.86 }}>
            <RevealLines lines={["Stop managing", "five agencies"]} dot />
          </motion.h2>
          <motion.p variants={rise} className="text-white/60 text-base sm:text-lg leading-relaxed max-w-xl mx-auto mb-9">
            Run one system. Tell us your problem — we'll show you the approach, the solve, the outcome, and what it costs versus what you're paying now.
          </motion.p>
          <motion.div variants={rise} className="flex flex-col items-center gap-3">
            <a href="mailto:hello@dentsuamp.com?subject=New%20project%20—%20dentsuAMP"
              className="group relative inline-flex items-center gap-2.5 px-9 py-4 rounded-full bg-[#E31937] text-white text-sm font-semibold overflow-hidden">
              <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" style={{ background: "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.35) 50%, transparent 65%)" }} />
              hello@dentsuamp.com <ArrowRight size={15} />
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-8 bg-[#050505]">
        <div className="max-w-[1280px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-[15px] tracking-[0.14em] uppercase font-bold">
            <span className="text-white">DENTSU </span><span className="text-[#E31937]">AMP</span>
          </span>
          <span className="text-white/25 text-[11px] tracking-[0.15em] uppercase" style={mono}>Built on dentsu's production heritage · Mumbai, India</span>
          <div className="flex items-center gap-5">
            <a href="mailto:hello@dentsuamp.com?subject=New%20project%20—%20dentsuAMP" className="text-white/45 text-[12px] tracking-[0.05em] hover:text-[#ff6b81] transition-colors" style={mono}>hello@dentsuamp.com</a>
            <button onClick={() => navigate("/login")} className="text-white/30 text-[11px] tracking-[0.2em] uppercase hover:text-[#E31937] transition-colors" style={mono}>
              Team Login →
            </button>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes heroAura { 0%,100% { opacity:.7; transform:scale(1);} 50% { opacity:1; transform:scale(1.06);} }
        @keyframes ticker { 0% { transform:translateX(0);} 100% { transform:translateX(-50%);} }
        @keyframes pulseRing { 0% { transform:scale(1); opacity:.8; } 100% { transform:scale(1.9); opacity:0; } }
        @keyframes ampGlow { 0%,100% { box-shadow:0 28px 80px -24px rgba(227,25,55,0.45); } 50% { box-shadow:0 32px 100px -18px rgba(227,25,55,0.72); } }
        @keyframes scanY { 0% { transform:translateY(-100%);} 100% { transform:translateY(430%);} }
        @keyframes livePulse { 0%,100% { opacity:1; box-shadow:0 0 0 0 rgba(227,25,55,0.6);} 50% { opacity:.5; box-shadow:0 0 0 4px rgba(227,25,55,0);} }
        .red-grain::before { content:""; position:absolute; inset:0; pointer-events:none; opacity:0.5; mix-blend-mode:multiply; background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.18'/%3E%3C/svg%3E"); }
        @media (prefers-reduced-motion: reduce) { [style*="scanY"], [style*="livePulse"] { animation:none !important; } }
        .amp-pop { animation: ampGlow 3.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .amp-pop { animation: none; box-shadow:0 28px 80px -24px rgba(227,25,55,0.45); } }
        .outline-text { color: transparent; -webkit-text-stroke: 1.5px rgba(255,255,255,0.25); }
        .outline-text-dark { color: transparent; -webkit-text-stroke: 1.5px rgba(10,10,10,0.3); }
        .hide-scroll::-webkit-scrollbar { display:none; }
        .hide-scroll { scrollbar-width: none; }
        .amp-range { -webkit-appearance:none; appearance:none; height:4px; border-radius:2px; background:rgba(255,255,255,0.16); outline:none; cursor:pointer; }
        .amp-range::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; width:18px; height:18px; border-radius:50%; background:#E31937; cursor:pointer; box-shadow:0 0 0 5px rgba(227,25,55,0.18); transition:box-shadow .2s; }
        .amp-range::-webkit-slider-thumb:hover { box-shadow:0 0 0 8px rgba(227,25,55,0.22); }
        .amp-range::-moz-range-thumb { width:18px; height:18px; border:none; border-radius:50%; background:#E31937; cursor:pointer; }
        html { scroll-behavior: smooth; }
        ::selection { background:#E31937; color:#fff; }
      `}</style>
    </div>
  );
}
